import {
  ChangeDetectionStrategy, Component, ElementRef, computed, effect, inject, input, output, viewChild,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { PaperButtonComponent, PaperStateComponent } from '@ricoh-paper/angular';
import { LocaleService } from '../../../../../core/services/locale.service';
import { quickOpenRows } from '../models/cmdk.model';
import type { CmdkGroup, CmdkScope, CmdkScopeHint } from '../models/cmdk.model';
import type { CmdkItem, CmdkRenderRow } from '../models/cmdk.model';
import { CmdkStateService } from '../services/cmdk-state.service';
import { CmdkSearchService } from '../services/cmdk-search.service';
import { CmdkRowCommandComponent } from './rows/cmdk-row-command.component';
import { CmdkRowAppComponent } from './rows/cmdk-row-app.component';
import { CmdkRowAskComponent } from './rows/cmdk-row-ask.component';
import { CmdkRowNewsComponent } from './rows/cmdk-row-news.component';
import { CmdkRowPersonComponent } from './rows/cmdk-row-person.component';
import { CmdkRowSpaceComponent } from './rows/cmdk-row-space.component';

/**
 * The left column: `role="listbox"` over the flat render list.
 *
 * AI CONTEXT — scrolling, and why not `scrollIntoView`
 *
 * `scrollIntoView({block:'nearest'})` is what the legacy palette uses
 * (`command-palette.component.ts:328`) and it is wrong here for two reasons.
 * It scrolls the nearest scrollable ANCESTOR, which inside a two-pane grid can
 * be the palette body rather than the results column — so arrowing the list
 * can slide the whole panel. And it gives no control over the margin, so the
 * selected row lands flush against the edge with no hint that there is more
 * below it.
 *
 * This sets `scrollTop` directly against the column, with an 8px margin, which
 * is the behaviour the prototype specified and the only one that keeps the
 * next row peeking.
 */
@Component({
  selector: 'im-cmdk-results-pane',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    PaperButtonComponent,
    PaperStateComponent,
    CmdkRowCommandComponent,
    CmdkRowAppComponent,
    CmdkRowAskComponent,
    CmdkRowSpaceComponent,
    CmdkRowPersonComponent,
    CmdkRowNewsComponent,
  ],
  templateUrl: './cmdk-results-pane.component.html',
  styleUrl: './cmdk-results-pane.component.scss',
})
export class CmdkResultsPaneComponent {
  private readonly state = inject(CmdkStateService);
  private readonly search = inject(CmdkSearchService);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  readonly listboxId = input.required<string>();
  /** True while ⌘ (or Ctrl) is held — reveals the quick-open badges. */
  readonly modifierHeld = input(false);
  /** Accessible name for the listbox. */
  readonly listLabel = input('');

  readonly activated = output<CmdkItem>();
  /** A hint switched the chip; the palette puts focus back in the field. */
  readonly scopeChosen = output<void>();

  private readonly scroller = viewChild<ElementRef<HTMLElement>>('scroller');

  protected readonly rows = this.search.rows;
  protected readonly selectedIndex = this.state.selectedIndex;
  protected readonly hints = this.search.emptyTabHints;

  protected readonly hintsHeading = computed(() => {
    this.locale.locale();
    return this.transloco.translate('shell.cmdk.elsewhere.heading');
  });

  /** "2 matches in People". */
  protected hintLabel(hint: CmdkScopeHint): string {
    return this.transloco.translate(
      hint.count === 1 ? 'shell.cmdk.elsewhere.one' : 'shell.cmdk.elsewhere.other',
      { count: hint.count, scope: hint.label },
    );
  }

  /** Switch the chip to the tab that has the matches. */
  protected showScope(scope: CmdkScope): void {
    this.state.setScope(scope);
    this.scopeChosen.emit();
  }

  /**
   * The no-matches copy. Query-aware, because "No matches" on its own leaves
   * the user unsure whether the palette searched at all — echoing what was
   * typed is the cheapest possible proof that it did.
   *
   * When a scope chip is active the miss is usually the FILTER rather than the
   * query, so that variant names the scope and points at Tab instead of
   * suggesting a rewording that will not help.
   */
  protected readonly emptyTitle = computed(() =>
    this.transloco.translate('shell.cmdk.no_matches.title'),
  );

  protected readonly emptyDescription = computed(() => {
    const query = this.state.query().trim();
    const scope = this.state.scope();
    if (scope === 'all') {
      return this.transloco.translate('shell.cmdk.no_matches.description', { query });
    }
    return this.transloco.translate('shell.cmdk.no_matches.description_scoped', {
      query,
      scope: this.transloco.translate(`shell.cmdk.scope.${scope}`),
    });
  });

  /**
   * flatIndex → ⌘N position, for the rows that carry a badge. The ask row is
   * selectable but never numbered, so this cannot be `flatIndex` itself.
   */
  protected readonly quickOpenByFlatIndex = computed(() => {
    const map = new Map<number, number>();
    quickOpenRows(this.rows()).forEach((row, position) => {
      if (position < 9) map.set(row.flatIndex, position);
    });
    return map;
  });

  constructor() {
    // Pull the selection back inside the list whenever it shrinks — otherwise
    // a stale index points past the end and the preview pane goes blank for
    // no visible reason.
    effect(() => this.state.clampSelection(this.search.resultCount()));

    // Follow the selection. Reading `selectedIndex` and `rows` makes this
    // re-run on both a keyboard move and a list change.
    effect(() => {
      const index = this.state.selectedIndex();
      this.rows();
      requestAnimationFrame(() => this.revealRow(index));
    });
  }

  /** Reload a group whose source failed. */
  protected retryGroup(group: CmdkGroup): void {
    this.search.retryGroup(group);
  }

  /** Reset the scroll position — the palette calls this on query change. */
  scrollToTop(): void {
    const el = this.scroller()?.nativeElement;
    if (el) el.scrollTop = 0;
  }

  protected isSelected(row: Extract<CmdkRenderRow, { type: 'row' }>): boolean {
    return row.flatIndex === this.state.selectedIndex();
  }

  protected onHover(row: Extract<CmdkRenderRow, { type: 'row' }>): void {
    this.state.setSelectedIndex(row.flatIndex);
  }

  protected onClick(row: Extract<CmdkRenderRow, { type: 'row' }>): void {
    this.state.setSelectedIndex(row.flatIndex);
    this.activated.emit(row.item);
  }

  /** Narrow the union for the template — Angular cannot do it inline. */
  protected asCommandItem(item: CmdkItem): Extract<CmdkItem, { kind: 'nav' | 'action' }> {
    return item as Extract<CmdkItem, { kind: 'nav' | 'action' }>;
  }

  protected asAppItem(item: CmdkItem): Extract<CmdkItem, { kind: 'app' }> {
    return item as Extract<CmdkItem, { kind: 'app' }>;
  }

  protected asAskItem(item: CmdkItem): Extract<CmdkItem, { kind: 'ask' }> {
    return item as Extract<CmdkItem, { kind: 'ask' }>;
  }

  protected asSpaceItem(item: CmdkItem): Extract<CmdkItem, { kind: 'space' }> {
    return item as Extract<CmdkItem, { kind: 'space' }>;
  }

  protected asPersonItem(item: CmdkItem): Extract<CmdkItem, { kind: 'person' }> {
    return item as Extract<CmdkItem, { kind: 'person' }>;
  }

  protected asNewsItem(item: CmdkItem): Extract<CmdkItem, { kind: 'news' }> {
    return item as Extract<CmdkItem, { kind: 'news' }>;
  }

  private revealRow(index: number): void {
    const column = this.scroller()?.nativeElement;
    if (!column) return;
    const target = column.querySelector<HTMLElement>(`[data-flat-index="${index}"]`);
    if (!target) return;

    const margin = 8;
    const top = target.offsetTop;
    const bottom = top + target.offsetHeight;
    if (top < column.scrollTop) {
      column.scrollTop = Math.max(0, top - margin);
    } else if (bottom > column.scrollTop + column.clientHeight) {
      column.scrollTop = bottom - column.clientHeight + margin;
    }
  }
}
